// DAT-25: "SBA programs" on the company record (REG-15 host mounts it after
// "Where they show up"). Certifications come from the public SAM cache
// ("SBA certified" vs "Self-represented in SAM"); agreements from SBA's public
// mentor-protégé list. SBS is a link-out only.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraEntityCell, SpecraEntityMatchConfirm, SpecraKeyValueRow, SpecraLink, SpecraSectionHeader, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { KIND_TO_PROTO, ResolveMode, useDecideRegistryMatchCandidate, useResolveEntities } from '../../../../components/SharedRecord/registryResolveClient';
import { useTenantAreas } from '../../../../lib/access/useTenantAreas';
import { formatDayMonthYear, formatPublicProvenance } from '../../../../lib/publicProvenance';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { SBSProfileURL } from '../../../../lib/sbaLinks';
import { registryHref } from '../../registryHref';
import { useCompanySbaProfile } from './sbaClient';
/** Canonical SBA-list tokens → display labels ("SBA list: 8(a), HUBZone"). */ const LIST_LABELS = {
    '8A': '8(a)',
    HUBZONE: 'HUBZone',
    WOSB: 'WOSB',
    EDWOSB: 'EDWOSB',
    SDVOSB: 'SDVOSB',
    VOSB: 'VOSB',
    SDB: 'SDB',
    SMALL: 'Small'
};
function day(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return iso;
    return formatDayMonthYear(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}
function listTypes(link) {
    return [
        ...link.certTypes.map((c)=>LIST_LABELS[c] ?? c),
        ...link.certTypesUnmapped
    ].join(', ');
}
/** Short labels for header descriptions and pursuit badges (≤ 3, ended excluded). */ export function sbaCertLabels(certs, max = 3) {
    const today = new Date().toISOString().slice(0, 10);
    const out = [];
    for (const c of certs){
        if (c.exitDate && c.exitDate < today) continue;
        if (!out.includes(c.label)) out.push(c.label);
        if (out.length >= max) break;
    }
    return out;
}
function CertValue({ cert }) {
    const today = new Date().toISOString().slice(0, 10);
    const ended = !!cert.exitDate && cert.exitDate < today;
    const prov = formatPublicProvenance({
        source: cert.source,
        asOf: cert.asOf,
        stale: cert.stale
    });
    let badge = /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: "neutral",
        children: "Self-represented in SAM"
    });
    if (cert.basis === 'sba_certified') {
        badge = ended ? /*#__PURE__*/ _jsxs(SpecraBadge, {
            variant: "warning",
            children: [
                "Ended ",
                day(cert.exitDate)
            ]
        }) : /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: "success",
            children: "SBA certified"
        });
    }
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-2",
        children: [
            badge,
            /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: prov.tooltip,
                placement: "top",
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: prov.label
                    })
                })
            })
        ]
    });
}
function MpRow({ link, asOf, canWrite }) {
    const resolve = useResolveEntities();
    const decide = useDecideRegistryMatchCandidate();
    const other = link.other;
    const href = other?.registryId ? registryHref({
        kind: 'COMPANY',
        id: other.registryId
    }) : null;
    const title = href ? /*#__PURE__*/ _jsx(SpecraLink, {
        href: href,
        renderLink: renderRouterLink,
        children: other?.name
    }) : other?.name ?? '';
    const types = listTypes(link);
    const description = [
        `Approved ${day(link.approvedOn)}`,
        types ? `SBA list: ${types}` : '',
        other?.state ?? ''
    ].filter(Boolean).join(' · ');
    const label = link.role === 'MENTOR' ? 'Mentor to' : 'Protégé of';
    const promote = ()=>resolve.mutate({
            mode: ResolveMode.MATCH_OR_CREATE,
            observation: {
                kind: KIND_TO_PROTO.COMPANY,
                name: other?.name ?? '',
                uei: other?.uei ?? '',
                publicKey: other?.publicKey?.startsWith('uei:') ? other.publicKey : '',
                source: 'sba.mp_xlsx',
                sourceLabel: `SBA mentor-protégé list · as of ${asOf}`
            }
        });
    return /*#__PURE__*/ _jsx("div", {
        "data-role": link.role,
        "data-testid": Testids.registrySba.mpRow,
        children: /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
            label: label,
            value: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        description: description,
                        title: title,
                        trailing: link.endedOn ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                            variant: "neutral",
                            children: [
                                "Ended ",
                                day(link.endedOn)
                            ]
                        }) : !other?.registryId && canWrite && !link.candidateId ? /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.registrySba.addToRegistry,
                            disabled: resolve.isPending || resolve.isSuccess,
                            onClick: promote,
                            size: "sm",
                            variant: "secondary",
                            children: resolve.isSuccess ? 'Added' : 'Add to registry'
                        }) : undefined
                    }),
                    link.candidateId && link.candidateOptions.length > 0 ? /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
                        candidates: link.candidateOptions.map((o)=>({
                                id: o.registryId,
                                name: o.name,
                                href: registryHref({
                                    kind: 'COMPANY',
                                    id: o.registryId
                                }) ?? undefined
                            })),
                        density: "inline",
                        disabled: !canWrite || decide.isPending,
                        kind: "COMPANY",
                        observedContext: other?.state || undefined,
                        observedLabel: other?.name ?? '',
                        observedSource: "SBA mentor-protégé list",
                        onConfirm: (id)=>decide.mutate({
                                candidateId: link.candidateId,
                                decision: 'CONFIRM',
                                chosenRegistryId: id
                            }),
                        readOnly: !canWrite,
                        renderLink: renderRouterLink,
                        size: "sm",
                        state: decide.isSuccess ? 'linked' : 'ask'
                    }) : null,
                    link.role === 'MENTOR' && !link.endedOn ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: "A mentor-protégé joint venture can compete for set-asides the protégé qualifies for (SBA approval required)."
                    }) : null
                ]
            })
        })
    });
}
export function SbaProgramsSection({ companyId, companyName, hideSbsLink }) {
    const profile = useCompanySbaProfile(companyId);
    const areas = useTenantAreas();
    const canWrite = areas.registries || areas.isAdmin || areas.isInternal;
    const [showPast, setShowPast] = useState(false);
    const data = profile.data;
    const sbsUrl = data?.sbsUrl || SBSProfileURL(data?.uei, data?.cage);
    const header = /*#__PURE__*/ _jsx(SpecraSectionHeader, {
        action: hideSbsLink ? undefined : /*#__PURE__*/ _jsx(SpecraLink, {
            "data-testid": Testids.registrySba.sbsLink,
            href: sbsUrl,
            rel: "noopener noreferrer",
            target: "_blank",
            children: "View in SBA Small Business Search"
        }),
        title: "SBA programs",
        variant: "group"
    });
    if (profile.isLoading) {
        return /*#__PURE__*/ _jsxs("section", {
            className: "space-y-3",
            "data-testid": Testids.registrySba.section,
            children: [
                header,
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Loading SBA programs…"
                })
            ]
        });
    }
    if (profile.isError || !data) {
        return /*#__PURE__*/ _jsxs("section", {
            className: "space-y-3",
            "data-testid": Testids.registrySba.section,
            children: [
                header,
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "SBA programs aren't available right now."
                })
            ]
        });
    }
    const certs = data.certifications;
    const active = data.mentorProtege.filter((l)=>!l.endedOn);
    const past = data.mentorProtege.filter((l)=>!!l.endedOn);
    const snap = data.snapshot;
    const asOf = snap?.asOf ? day(snap.asOf) : '';
    const small = data.naicsSmall;
    const totalNaics = small.length + data.naicsOtherThanSmall.length;
    const empty = certs.length === 0 && data.mentorProtege.length === 0 && totalNaics === 0;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": Testids.registrySba.section,
        children: [
            header,
            !data.uei ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                description: "Certifications come from SAM.gov; agreements from SBA's public list.",
                size: "compact",
                title: "Add this company's UEI to see SBA certifications."
            }) : empty ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                description: "Certifications come from SAM.gov; agreements from SBA's public list.",
                size: "compact",
                title: `No SBA certifications or mentor-protégé agreements found for ${companyName}.`
            }) : null,
            certs.map((c)=>/*#__PURE__*/ _jsx("div", {
                    "data-code": c.code,
                    "data-testid": Testids.registrySba.certRow,
                    children: /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: c.label,
                        value: /*#__PURE__*/ _jsx(CertValue, {
                            cert: c
                        })
                    })
                }, `${c.basis}:${c.code}`)),
            totalNaics > 0 ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Small for NAICS",
                value: /*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: small.length ? small.join(', ') : 'None',
                    placement: "top",
                    children: /*#__PURE__*/ _jsx("span", {
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "body-ui",
                            children: [
                                "Small for ",
                                small.length,
                                " of ",
                                totalNaics,
                                " NAICS codes"
                            ]
                        })
                    })
                })
            }) : null,
            active.map((l)=>/*#__PURE__*/ _jsx(MpRow, {
                    asOf: asOf,
                    canWrite: canWrite,
                    link: l
                }, `${l.role}:${l.other?.publicKey}`)),
            past.length > 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        "data-testid": Testids.registrySba.pastToggle,
                        onClick: ()=>setShowPast((v)=>!v),
                        size: "sm",
                        variant: "ghost",
                        children: [
                            "Past agreements (",
                            past.length,
                            ")"
                        ]
                    }),
                    showPast ? past.map((l)=>/*#__PURE__*/ _jsx(MpRow, {
                            asOf: asOf,
                            canWrite: canWrite,
                            link: l
                        }, `${l.role}:${l.other?.publicKey}`)) : null
                ]
            }) : null,
            snap?.loaded ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    "From SBA's active mentor-protégé list, as of ",
                    asOf,
                    ".",
                    snap.stale ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "warning",
                                children: "may be out of date"
                            })
                        ]
                    }) : null
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "SBA's mentor-protégé list has not been loaded in this deployment yet."
            })
        ]
    });
}
